import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getStatusColor, getStatusLabel, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user, hasRole } = useAuth();
  const { theme } = useTheme();
  
  // Check if user can delete incidents (responsable or admin only)
  const canDeleteIncidents = hasRole(['admin', 'responsable']);

  const [newIncident, setNewIncident] = useState({
    titre: '',
    description: '',
    type: 'vehicule',
    priority: 'moyenne'
  });

  useEffect(() => {
    fetchIncidents();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = incidents.filter(i =>
        `${i.titre} ${i.description}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredIncidents(filtered);
    } else {
      setFilteredIncidents(incidents);
    }
  }, [searchTerm, incidents]);

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await api.get('/incidents');
      setIncidents(response.data);
      setFilteredIncidents(response.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Erreur lors du chargement des incidents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  // Smart refresh: WebSocket + polling fallback 30s
  useSmartRefresh(fetchIncidents, {
    wsEvents: [WS_EVENTS.INCIDENT_CREATED, WS_EVENTS.INCIDENT_UPDATED],
    fallbackInterval: 30000
  });

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    try {
      await api.post('/incidents', newIncident);
      toast.success('Incident créé avec succès');
      setDialogOpen(false);
      setNewIncident({ titre: '', description: '', type: 'vehicule', priority: 'moyenne' });
      fetchIncidents();
    } catch (error) {
      console.error('Error creating incident:', error);
      toast.error('Erreur lors de la création de l\'incident');
    }
  };

  const handleUpdateStatus = async (incidentId, newStatus) => {
    try {
      await api.put(`/incidents/${incidentId}`, { statut: newStatus });
      toast.success('Statut mis à jour');
      fetchIncidents();
    } catch (error) {
      console.error('Error updating incident:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    if (!window.confirm('Supprimer cet incident ?')) return;
    try {
      await api.delete(`/incidents/${incidentId}`);
      toast.success('Incident supprimé');
      fetchIncidents();
    } catch (error) {
      console.error('Error deleting incident:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la suppression');
    }
  };

  const getStatusBadgeClass = (statut) => {
    switch (statut) {
      case 'ouvert':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'en_cours':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'resolu':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'ferme':
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="incidents-page">
      {/* Header - Search + Button on same line on mobile */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className={cn("absolute left-2.5 top-1/2 transform -translate-y-1/2", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')} size={16} />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              "pl-8 h-9 text-sm",
              theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
            )}
            data-testid="search-input"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="brand" data-testid="add-incident-button" className="h-9 w-9 p-0 shrink-0">
              <Plus size={16} />
            </Button>
          </DialogTrigger>
          <DialogContent className={cn(
            "border",
            theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
          )}>
            <DialogHeader>
              <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Nouvel incident</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateIncident} className="space-y-4">
              <div>
                <Label htmlFor="titre" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Titre</Label>
                <Input
                  id="titre"
                  value={newIncident.titre}
                  onChange={(e) => setNewIncident({...newIncident, titre: e.target.value})}
                  required
                  className={cn(
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                />
              </div>
              <div>
                <Label htmlFor="description" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Description</Label>
                <Textarea
                  id="description"
                  value={newIncident.description}
                  onChange={(e) => setNewIncident({...newIncident, description: e.target.value})}
                  rows={3}
                  required
                  className={cn(
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}
                />
              </div>
              <div>
                <Label htmlFor="type" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Type</Label>
                <Select
                  value={newIncident.type}
                  onValueChange={(value) => setNewIncident({...newIncident, type: value})}
                >
                  <SelectTrigger className={cn(
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={cn(
                    theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
                  )}>
                    <SelectItem value="salarie">Salarié</SelectItem>
                    <SelectItem value="vehicule">Véhicule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Priorité</Label>
                <Select
                  value={newIncident.priority}
                  onValueChange={(value) => setNewIncident({...newIncident, priority: value})}
                >
                  <SelectTrigger className={cn(
                    theme === 'dark' ? 'bg-zinc-900 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={cn(
                    theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200'
                  )}>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="moyenne">Moyenne</SelectItem>
                    <SelectItem value="basse">Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" variant="brand" className="w-full">Créer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {filteredIncidents.map((incident) => (
          <div
            key={incident.id}
            className={cn(
              "p-5 rounded-xl border transition-all",
              theme === 'dark' ? 'bg-black/50 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
            )}
            data-testid={`incident-card-${incident.id}`}
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="font-semibold text-lg text-white">{incident.titre}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeClass(incident.statut)}`}>
                    {getStatusLabel(incident.statut)}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium border bg-zinc-700/50 text-zinc-300 border-zinc-600 capitalize">
                    {incident.type}
                  </span>
                </div>
                <p className="text-sm text-zinc-400 mb-3">{incident.description}</p>
                <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                  <span>Créé par: {incident.created_by_nom}</span>
                  <span>{formatDateTime(incident.created_at)}</span>
                  {incident.assigned_to_nom && (
                    <span>Assigné à: {incident.assigned_to_nom}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {incident.statut === 'ouvert' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(incident.id, 'en_cours')}
                    className="bg-zinc-800/50 border-white/10 text-white hover:bg-white/10"
                  >
                    Prendre en charge
                  </Button>
                )}
                {incident.statut === 'en_cours' && (
                  <Button
                    size="sm"
                    onClick={() => handleUpdateStatus(incident.id, 'resolu')}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    Résoudre
                  </Button>
                )}
                {incident.statut === 'resolu' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(incident.id, 'ferme')}
                    className="bg-zinc-800/50 border-white/10 text-white hover:bg-white/10"
                  >
                    Fermer
                  </Button>
                )}
                {canDeleteIncidents && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteIncident(incident.id)}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    title="Supprimer"
                    data-testid={`delete-incident-${incident.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredIncidents.length === 0 && (
        <div className="text-center py-12 glass-panel">
          <p className="text-zinc-500">Aucun incident trouvé</p>
        </div>
      )}
    </div>
  );
}
